import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { publicApi } from "../lib/api";
import { Rocket, ShieldCheck, CheckCircle2, XCircle, Upload, Link2, Anchor } from "lucide-react";

import { _t, _locale } from "@/lib/i18n";
function formatDT(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(_locale(), { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

export default function AnchorVerify() {
  const { anchor_id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadResult, setUploadResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: res } = await publicApi.get(`/verifica-sigillo/${anchor_id}`);
        setData(res);
      } catch { setData({ exists: false, reason: _t("Errore di rete") }); }
      finally { setLoading(false); }
    })();
  }, [anchor_id]);

  const uploadPdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadResult({ loading: true });
    try {
      const buf = await f.arrayBuffer();
      const { data: res } = await publicApi.post("/verifica-sigillo/upload", buf, {
        headers: { "Content-Type": "application/pdf" },
      });
      setUploadResult(res);
    } catch { setUploadResult({ error: true }); }
    e.target.value = "";
  };

  const a = data?.anchor;
  const ic = data?.integrity_check;

  return (
    <div className="min-h-screen">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/images/img1.png" alt={_t("Time Capsule")} className="w-9 h-9 rounded-lg" />
            <div className="font-display text-2xl text-white">{_t("Time Capsule")}</div>
          </Link>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">{_t("Verifica sigillo")}</div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#33B2FF] mb-3 flex items-center gap-2">
          <Anchor className="w-3 h-3" />{" "}{_t("Certificato di sigillo")}</div>
        <h1 className="font-display text-5xl text-white mb-8">
          {loading ? _t("Verifica in corso...") : data?.exists ? _t("Ancoraggio trovato") : _t("Ancoraggio non trovato")}
        </h1>

        {loading ? null : !data?.exists ? (
          <div className="tc-card p-8" data-testid="anchor-not-found">
            <XCircle className="w-10 h-10 text-red-400 mb-4" />
            <p className="text-white/70">{_t("Nessun ancoraggio registrato con ID")}{" "}<span className="font-mono-launch text-white">{anchor_id}</span>{_t(". Verifica il codice o il QR code sul certificato.")}</p>
          </div>
        ) : (
          <>
            {/* Integrity check panel */}
            <div className={`p-5 rounded-2xl border mb-6 ${ic?.merkle_valid && ic?.block_hash_valid ? "bg-green-500/10 border-green-500/30" : "bg-red-500/10 border-red-500/30"}`} data-testid="integrity-panel">
              <div className="flex items-center gap-3 mb-3">
                {ic?.merkle_valid && ic?.block_hash_valid ? <CheckCircle2 className="w-6 h-6 text-green-400" /> : <XCircle className="w-6 h-6 text-red-400" />}
                <div>
                  <div className="font-display text-xl text-white">
                    {ic?.merkle_valid && ic?.block_hash_valid ? _t("Sigillo autentico e integro") : _t("Attenzione: integrità compromessa")}
                  </div>
                  <div className="text-xs text-white/60">{_t("Merkle root e block hash verificati contro il registro locale.")}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                <div className={ic?.merkle_valid ? "text-green-300" : "text-red-300"}>{_t("{0} Merkle root valida", ic?.merkle_valid ? "✓" : "✗")}</div>
                <div className={ic?.block_hash_valid ? "text-green-300" : "text-red-300"}>{_t("{0} Block hash valido", ic?.block_hash_valid ? "✓" : "✗")}</div>
              </div>
            </div>

            <div className="tc-card p-8 md:p-10 mb-6" data-testid="anchor-details">
              {a.is_demo && (
                <div className="mb-6 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-200 text-xs">
                  <strong>{_t("SIGILLO DEMO")}</strong>{" "}{_t("— Registrato su una catena locale hash-linked, non su una blockchain pubblica. Nessun valore legale.")}</div>
              )}

              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <Field label={_t("Anchor ID")} value={a.anchor_id} mono highlight />
                <Field label={_t("Block #")} value={`#${a.block_number}`} mono />
                <Field label={_t("Chain")} value={a.chain} />
                <Field label={_t("Provider")} value={a.provider} />
                <Field label={_t("Data creazione")} value={formatDT(a.created_at)} />
                <Field label={_t("Data apertura programmata")} value={formatDT(a.open_at)} highlight />
                <Field label={_t("Mittente (anonimizzato)")} value={a.anonymized_sender} mono />
                <Field label={_t("Destinatari")} value={String(a.recipients_count)} />
                <Field label={_t("Ancoraggio registrato")} value={formatDT(a.anchored_at)} />
              </div>

              <div className="pt-6 border-t border-white/10 space-y-4">
                <HashRow label={_t("Hash contenuto (SHA-256)")} value={a.content_hash} />
                <HashRow label={_t("Merkle root")} value={a.merkle_root} />
                <HashRow label={_t("Transaction ID (simulata)")} value={a.tx_id} highlight />
                <HashRow label={_t("Block hash")} value={a.block_hash} />
                <HashRow label={_t("Prev block hash")} value={a.prev_block_hash} />
              </div>

              <div className="mt-6 p-3 rounded-lg bg-white/5 border border-white/10 text-xs text-white/60 flex items-start gap-3">
                <ShieldCheck className="w-4 h-4 text-[#FF5722] mt-0.5 shrink-0" />
                <div>{_t("L'hash del contenuto identifica in modo univoco la capsula al momento del sigillo. Se la capsula (o il certificato PDF) fosse stato modificato, i valori qui riportati non corrisponderebbero al registro.")}</div>
              </div>
            </div>

            <div className="tc-card p-6 mb-6">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
                <Upload className="w-3.5 h-3.5" />{" "}{_t("Verifica hash di un PDF caricato")}</div>
              <label htmlFor="pdf-anchor-upload" className="cursor-pointer block border border-dashed border-white/15 hover:border-[#33B2FF] rounded-xl p-6 text-center transition-colors">
                <input id="pdf-anchor-upload" type="file" accept="application/pdf" onChange={uploadPdf} className="hidden" data-testid="anchor-pdf-upload" />
                <div className="text-sm text-white mb-1">{_t("Carica il certificato PDF per calcolarne l'hash")}</div>
                <div className="text-xs text-white/50">{_t("Il tuo file non viene conservato")}</div>
              </label>
              {uploadResult && !uploadResult.loading && (
                <div className="mt-4 p-4 rounded-xl bg-white/5 border border-white/10 text-xs">
                  {uploadResult.error ? _t("Errore di verifica.") : (
                    <>
                      <div className="text-white/60 mb-1">{_t("SHA-256 del PDF caricato:")}</div>
                      <div className="font-mono-launch text-white break-all">{uploadResult.pdf_sha256}</div>
                      <div className="text-white/50 mt-2">{uploadResult.note}</div>
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function Field({ label, value, mono, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">{label}</div>
      <div className={`${mono ? "font-mono-launch" : ""} ${highlight ? "text-[#FF5722] text-lg" : "text-white"} break-all`}>{value}</div>
    </div>
  );
}
function HashRow({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">{label}</div>
      <div className={`font-mono-launch text-xs break-all ${highlight ? "text-[#FF5722]" : "text-white/70"}`}>{value}</div>
    </div>
  );
}
